import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@material-ui/icons/Check';

/**
 * AccordionStep - Wrapper component for each wizard step
 * IMPORTANT: Always renders children to preserve form state, just hides them when not active
 * Click on header of completed step to edit (no summary shown)
 *
 * @param {boolean} props.hideOnDesktop - If true, hide this step on desktop (for summary step)
 */
const AccordionStep = ({
  stepNumber,
  title,
  status = 'pending',
  onEdit,
  children,
  hideOnDesktop = false
}) => {
  const { t } = useTranslation();
  const stepRef = useRef(null);
  const previousStatusRef = useRef(status);

  // Auto-scroll to step when it becomes active (for mobile)
  // Only scroll if status CHANGED to 'active', not if it started as 'active'
  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = status;

    // Only scroll if status changed from something else to 'active'
    if (status === 'active' && previousStatus !== 'active' && stepRef.current) {
      setTimeout(() => {
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
          const headerHeight = 60;
          const elementTop = stepRef.current.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementTop - headerHeight - 10,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [status]);

  const handleHeaderClick = () => {
    // Allow clicking completed steps to edit them
    if (status === 'completed' && onEdit) {
      console.log('AccordionStep: Clicking completed step', stepNumber);
      onEdit();
    }
  };

  // Desktop hiding class
  const desktopHideClass = hideOnDesktop ? 'desktop-hidden' : '';

  // Get appropriate aria attributes based on status
  const getAriaLabel = () => {
    if (status === 'completed') {
      return t('reservation.wizard.accordionStep.completed', { number: stepNumber, title });
    } else if (status === 'active') {
      return t('reservation.wizard.accordionStep.active', { number: stepNumber, title });
    }
    return t('reservation.wizard.accordionStep.pending', { number: stepNumber, title });
  };

  return (
    <div
      ref={stepRef}
      className={`accordion-step step-${stepNumber} ${status} ${desktopHideClass}`}
      role="region"
      aria-label={t('reservation.wizard.accordionStep.stepLabel', { number: stepNumber, title })}
    >
      {/* Step Header - clickable for completed steps */}
      <div
        className={`accordion-step-header ${status === 'completed' ? 'accordion-step-header-pointer' : 'accordion-step-header-default'}`}
        onClick={handleHeaderClick}
        role={status === 'completed' ? 'button' : 'heading'}
        aria-level={status === 'completed' ? undefined : 3}
        aria-label={getAriaLabel()}
        aria-expanded={status === 'active'}
        tabIndex={status === 'completed' ? 0 : -1}
        onKeyPress={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && status === 'completed') {
            handleHeaderClick();
          }
        }}
      >
        {status === 'completed' ? (
          <div className="step-checkmark" aria-hidden="true" title={t('reservation.wizard.accordionStep.stepCompleted')}>
            <CheckIcon className="accordion-step-checkmark-icon" />
          </div>
        ) : (
          <div className="step-number" aria-hidden="true">{stepNumber}</div>
        )}
        <div className="step-title">{title}</div>
      </div>

      {/* NO summary - completed steps just show header with checkmark */}

      {/* Step Content - ALWAYS render children to preserve form state, CSS hides/shows */}
      <div
        className="accordion-step-content"
        role="region"
        aria-hidden={status !== 'active'}
      >
        {children}
      </div>
    </div>
  );
};

export default AccordionStep;
